import React, { useState } from 'react';
import { X, Send, Star, MessageSquare, Sparkles } from 'lucide-react';
import { API_URL } from '../config/api.ts';

interface SuggestionsModalProps {
    isOpen: boolean;
    onClose: () => void;
}

const SuggestionsModal: React.FC<SuggestionsModalProps> = ({ isOpen, onClose }) => {
    const [feedback, setFeedback] = useState('');
    const [rating, setRating] = useState(0);
    const [hoverRating, setHoverRating] = useState(0);
    const [category, setCategory] = useState('general');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);

    const categories = [
        { id: 'general', label: 'General Feedback', emoji: '💬' },
        { id: 'feature', label: 'Feature Request', emoji: '✨' },
        { id: 'bug', label: 'Bug Report', emoji: '🐛' },
        { id: 'character', label: 'Character Ideas', emoji: '🎭' },
        { id: 'other', label: 'Other', emoji: '📝' }
    ];

    const handleSubmit = async () => {
        if (!feedback.trim()) return;

        setIsSubmitting(true);
        try {
            const response = await fetch(`${API_URL}/suggestions`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({
                    feedback,
                    rating,
                    category
                })
            });

            if (response.ok) {
                setSubmitted(true);
                setTimeout(() => {
                    onClose();
                    setSubmitted(false);
                    setFeedback('');
                    setRating(0);
                    setCategory('general');
                }, 2000);
            }
        } catch (error) {
            console.error('Error submitting suggestion:', error);
        } finally {
            setIsSubmitting(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div style={{
            position: 'fixed',
            inset: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.8)',
            backdropFilter: 'blur(8px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 2000,
            padding: '20px'
        }}>
            <div style={{
                background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f0f23 100%)',
                borderRadius: '24px',
                width: '100%',
                maxWidth: '500px',
                padding: '32px',
                position: 'relative',
                border: '1px solid rgba(168, 85, 247, 0.3)',
                boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5), 0 0 50px rgba(168, 85, 247, 0.15)'
            }}>
                {/* Close Button */}
                <button
                    onClick={onClose}
                    style={{
                        position: 'absolute',
                        top: '16px',
                        right: '16px',
                        background: 'rgba(255,255,255,0.1)',
                        border: 'none',
                        borderRadius: '50%',
                        width: '36px',
                        height: '36px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: 'pointer',
                        color: 'rgba(255,255,255,0.6)',
                        transition: 'all 0.2s'
                    }}
                >
                    <X size={18} />
                </button>

                {submitted ? (
                    /* Success State */
                    <div style={{ textAlign: 'center', padding: '40px 0' }}>
                        <div style={{
                            width: '80px',
                            height: '80px',
                            borderRadius: '50%',
                            background: 'linear-gradient(135deg, #10b981, #059669)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            margin: '0 auto 20px',
                            animation: 'scaleIn 0.3s ease-out'
                        }}>
                            <Sparkles size={40} color="white" />
                        </div>
                        <h2 style={{ color: 'white', fontSize: '24px', marginBottom: '8px' }}>
                            Thank You! 💜
                        </h2>
                        <p style={{ color: 'rgba(255,255,255,0.6)' }}>
                            Your feedback helps us improve EcstasyAI
                        </p>
                    </div>
                ) : (
                    <>
                        {/* Header */}
                        <div style={{ textAlign: 'center', marginBottom: '24px' }}>
                            <div style={{
                                width: '60px',
                                height: '60px',
                                borderRadius: '16px',
                                background: 'linear-gradient(135deg, #a855f7, #ec4899)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                margin: '0 auto 16px'
                            }}>
                                <MessageSquare size={28} color="white" />
                            </div>
                            <h2 style={{
                                color: 'white',
                                fontSize: '24px',
                                fontWeight: '700',
                                marginBottom: '8px'
                            }}>
                                Share Your Thoughts
                            </h2>
                            <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: '14px' }}>
                                Help us make EcstasyAI even better!
                            </p>
                        </div>

                        {/* Rating */}
                        <div style={{ marginBottom: '20px' }}>
                            <label style={{
                                display: 'block',
                                color: 'rgba(255,255,255,0.7)',
                                fontSize: '13px',
                                marginBottom: '10px',
                                fontWeight: '500'
                            }}>
                                How would you rate your experience?
                            </label>
                            <div style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
                                {[1, 2, 3, 4, 5].map((star) => (
                                    <button
                                        key={star}
                                        onClick={() => setRating(star)}
                                        onMouseEnter={() => setHoverRating(star)}
                                        onMouseLeave={() => setHoverRating(0)}
                                        style={{
                                            background: 'none',
                                            border: 'none',
                                            cursor: 'pointer',
                                            padding: '4px',
                                            transition: 'transform 0.2s'
                                        }}
                                    >
                                        <Star
                                            size={32}
                                            fill={(hoverRating || rating) >= star ? '#fbbf24' : 'transparent'}
                                            color={(hoverRating || rating) >= star ? '#fbbf24' : '#4b5563'}
                                            style={{ transition: 'all 0.2s' }}
                                        />
                                    </button>
                                ))}
                            </div>
                        </div>

                        {/* Category Selection */}
                        <div style={{ marginBottom: '20px' }}>
                            <label style={{
                                display: 'block',
                                color: 'rgba(255,255,255,0.7)',
                                fontSize: '13px',
                                marginBottom: '10px',
                                fontWeight: '500'
                            }}>
                                Category
                            </label>
                            <div style={{
                                display: 'flex',
                                gap: '8px',
                                flexWrap: 'wrap'
                            }}>
                                {categories.map((cat) => (
                                    <button
                                        key={cat.id}
                                        onClick={() => setCategory(cat.id)}
                                        style={{
                                            padding: '8px 14px',
                                            borderRadius: '20px',
                                            border: category === cat.id
                                                ? '1px solid #a855f7'
                                                : '1px solid rgba(255,255,255,0.1)',
                                            background: category === cat.id
                                                ? 'rgba(168, 85, 247, 0.2)'
                                                : 'rgba(255,255,255,0.05)',
                                            color: category === cat.id ? '#a855f7' : 'rgba(255,255,255,0.6)',
                                            fontSize: '13px',
                                            cursor: 'pointer',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '6px',
                                            transition: 'all 0.2s'
                                        }}
                                    >
                                        <span>{cat.emoji}</span>
                                        {cat.label}
                                    </button>
                                ))}
                            </div>
                        </div>

                        {/* Feedback Text */}
                        <div style={{ marginBottom: '24px' }}>
                            <label style={{
                                display: 'block',
                                color: 'rgba(255,255,255,0.7)',
                                fontSize: '13px',
                                marginBottom: '10px',
                                fontWeight: '500'
                            }}>
                                Your feedback
                            </label>
                            <textarea
                                value={feedback}
                                onChange={(e) => setFeedback(e.target.value)}
                                placeholder="Tell us what you think, suggest new features, or report issues..."
                                style={{
                                    width: '100%',
                                    minHeight: '120px',
                                    padding: '14px',
                                    borderRadius: '12px',
                                    border: '1px solid rgba(255,255,255,0.1)',
                                    background: 'rgba(0,0,0,0.3)',
                                    color: 'white',
                                    fontSize: '14px',
                                    resize: 'vertical',
                                    outline: 'none',
                                    fontFamily: 'inherit'
                                }}
                            />
                            <div style={{
                                textAlign: 'right',
                                fontSize: '12px',
                                color: 'rgba(255,255,255,0.4)',
                                marginTop: '4px'
                            }}>
                                {feedback.length} characters
                            </div>
                        </div>

                        {/* Submit Button */}
                        <button
                            onClick={handleSubmit}
                            disabled={!feedback.trim() || isSubmitting}
                            style={{
                                width: '100%',
                                padding: '14px',
                                borderRadius: '12px',
                                border: 'none',
                                background: feedback.trim()
                                    ? 'linear-gradient(135deg, #a855f7, #ec4899)'
                                    : 'rgba(255,255,255,0.1)',
                                color: feedback.trim() ? 'white' : 'rgba(255,255,255,0.4)',
                                fontSize: '15px',
                                fontWeight: '600',
                                cursor: feedback.trim() ? 'pointer' : 'not-allowed',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: '8px',
                                transition: 'all 0.2s',
                                boxShadow: feedback.trim()
                                    ? '0 4px 20px rgba(168, 85, 247, 0.4)'
                                    : 'none'
                            }}
                        >
                            {isSubmitting ? (
                                'Sending...'
                            ) : (
                                <>
                                    <Send size={18} />
                                    Submit Feedback
                                </>
                            )}
                        </button>
                    </>
                )}

                <style>{`
                    @keyframes scaleIn {
                        from { transform: scale(0); }
                        to { transform: scale(1); }
                    }
                `}</style>
            </div>
        </div>
    );
};

export default SuggestionsModal;


